import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const pages = [
    { name: "Home", path: "/", keywords: "landing main" },
    { name: "How it works", path: "/how-it-works", keywords: "nfc process steps" },
    { name: "Pricing", path: "/pricing", keywords: "plans cost price upgrade store" },
    { name: "My cards", path: "/my-card", keywords: "card dashboard edit links stats" },
    { name: "Insights", path: "/insights", keywords: "analytics views clicks" },
    { name: "Leads", path: "/leads", keywords: "contacts captured prospects" },
    { name: "Email signature", path: "/email-signature", keywords: "signature gmail outlook" },
    { name: "Order physical card", path: "/order-card", keywords: "order nfc physical card" },
    { name: "Profile", path: "/profile", keywords: "account settings info" },
    { name: "Help", path: "/help", keywords: "support faq" },
    { name: "Contact", path: "/contact", keywords: "help support faq" },
    { name: "Teams", path: "/teams", keywords: "team company enterprise bulk" },
    { name: "For recruiters", path: "/for/recruiters", keywords: "recruiters hiring career" },
    { name: "For sales", path: "/for/sales", keywords: "sales networking" },
    { name: "For real estate", path: "/for/real-estate", keywords: "real estate agents" },
    { name: "Terms of service", path: "/terms", keywords: "legal tos" },
    { name: "Privacy policy", path: "/privacy", keywords: "legal data" },
];

const CommandPalette: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const navigate = useNavigate();

    const filtered = pages.filter(p => {
        if (!query) return true;
        const q = query.toLowerCase();
        return p.name.toLowerCase().includes(q) || p.keywords.includes(q);
    });

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "k") {
            e.preventDefault();
            setOpen(prev => !prev);
            setQuery("");
            setSelectedIndex(0);
        }
        if (e.key === "Escape") {
            setOpen(false);
        }
    }, []);

    useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const handleSelect = (path: string) => {
        navigate(path);
        setOpen(false);
        setQuery("");
    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex(i => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Enter" && filtered[selectedIndex]) {
            handleSelect(filtered[selectedIndex].path);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
                <div className="p-4 border-b border-coffee-200">
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
                        onKeyDown={handleInputKeyDown}
                        placeholder="Search pages... (↑↓ to navigate, Enter to select)"
                        className="w-full px-4 py-3 text-coffee-900 bg-coffee-50 rounded-lg focus:outline-none focus:ring-2 focus:ring-coffee-500"
                        autoFocus
                    />
                </div>
                <div className="max-h-80 overflow-y-auto">
                    {filtered.length > 0 ? filtered.map((page, i) => (
                        <button
                            key={page.path}
                            onClick={() => handleSelect(page.path)}
                            className={`w-full text-left px-6 py-3 flex items-center justify-between transition-colors ${
                                i === selectedIndex ? 'bg-coffee-100 text-coffee-900' : 'text-coffee-700 hover:bg-coffee-50'
                            }`}
                        >
                            <span className="font-medium">{page.name}</span>
                            <span className="text-xs text-coffee-400">{page.path}</span>
                        </button>
                    )) : (
                        <div className="px-6 py-8 text-center text-coffee-500">
                            No pages found
                        </div>
                    )}
                </div>
                <div className="p-3 border-t border-coffee-200 text-xs text-coffee-400 flex justify-between">
                    <span>↑↓ Navigate</span>
                    <span>Enter Select</span>
                    <span>Esc Close</span>
                </div>
            </div>
        </div>
    );
};

export default CommandPalette;
